// calculateTaxes — faithful port of OrderCreateViewModel.kt:4511-4669.
//
// Tax base is per-line jsonNewPrice (set by calculatePennyDifference), NOT catalog price.
// Per (line, tax): taxAmount = roundHalfUp(jsonNewPrice * percent / 100), NaN -> 0.
// name = displayName || title.
// Per-line buckets: DefaultTax -> perLineDefaultTax[orderId]; others -> perLineOtherTax[orderId][].
// taxByName accumulates a running total per tax name (sum of the rounded per-line amounts);
// taxRateInAmount = sum of taxByName prices.
//
// NOTE (verify): Android iterates getCartListPenny(); for v1 (no split) we exclude the same
// synthetic lines calculateSubtotal excludes. Lottery/scratcher lines carry no taxList.

import { roundHalfUp, toNum } from "./money";

// Android 97cd8159fc (all stores): tax is rounded on the CUMULATIVE taxable per tax name and each
// line takes the delta (2 × 2.625 → 5.25, not 5.26). A parallel BASE accumulator (dual price) taxes
// max(0, toBase(jsonNewPrice + jsonLoyaltyAmt) − jsonLoyaltyAmt) the same way → baseTaxRateInAmount
// and per-line perLineBaseDefaultTax (Android hashMapBaseDefaultTaxPerItem).
export function calculateTaxes(cart, { toBase = (v) => roundHalfUp(v) } = {}) {
  const perLineDefaultTax = new Map(); // orderId -> { title, displayName, percent, price }
  const perLineOtherTax = new Map(); // orderId -> [ { title, displayName, percent, price } ]
  const perLineBaseDefaultTax = new Map(); // orderId -> base default-tax delta
  const taxByName = new Map(); // name -> { name, percent, price } (running total)
  const cumTaxable = new Map();
  const cumRounded = new Map();
  const cumBaseTaxable = new Map();
  const cumBaseRounded = new Map();
  let taxRateInAmount = 0;

  for (const line of cart) {
    if (line.isSplitPaymentDiscount || line.isSplitPayment || line.isSplitRefund) continue;
    if (line.isScratcherPayout || line.isLotteryPayout || line.isLotteryTicket === "1") continue; // Android excludes lottery/scratcher from tax
    const taxList = Array.isArray(line.taxList) ? line.taxList : [];

    for (const taxModel of taxList) {
      const pct = toNum(taxModel.percent);
      const name = taxModel.displayName ? taxModel.displayName : taxModel.title;

      const lineTaxable = Math.max(toNum(line.jsonNewPrice), 0);
      const newCum = (cumTaxable.get(name) || 0) + lineTaxable;
      cumTaxable.set(name, newCum);
      const newCumRounded = roundHalfUp((newCum * pct) / 100);
      let taxAmount = roundHalfUp(newCumRounded - (cumRounded.get(name) || 0)); // a delta of 2-dp values — rounding only drops float noise
      cumRounded.set(name, newCumRounded);
      if (isNaN(taxAmount)) taxAmount = 0;

      const lp = toNum(line.jsonLoyaltyAmt);
      const baseTaxable = Math.max(toBase(toNum(line.jsonNewPrice) + lp) - lp, 0);
      const newCumBase = (cumBaseTaxable.get(name) || 0) + baseTaxable;
      cumBaseTaxable.set(name, newCumBase);
      const newCumBaseRounded = roundHalfUp((newCumBase * pct) / 100);
      const baseTaxAmount = roundHalfUp(newCumBaseRounded - (cumBaseRounded.get(name) || 0));
      cumBaseRounded.set(name, newCumBaseRounded);

      // Android shows the rate AS STORED ("7.255%", "8%") — keep the raw string next to the number.
      const percentRaw = taxModel.percent == null ? "" : String(taxModel.percent).trim();
      const existing = taxByName.get(name);
      if (existing) existing.price += taxAmount;
      else taxByName.set(name, { name, percent: pct, percentRaw, price: taxAmount });

      const copy = {
        title: taxModel.title,
        displayName: taxModel.displayName ?? null,
        percent: pct,
        percentRaw,
        price: taxAmount,
      };
      if (taxModel.title === "DefaultTax") {
        perLineDefaultTax.set(line.orderId, copy);
        perLineBaseDefaultTax.set(line.orderId, isNaN(baseTaxAmount) ? 0 : baseTaxAmount);
      } else {
        const list = perLineOtherTax.get(line.orderId) || [];
        list.push(copy);
        perLineOtherTax.set(line.orderId, list);
      }
    }
  }

  for (const v of taxByName.values()) taxRateInAmount += v.price;
  let baseTaxRateInAmount = 0;
  for (const v of cumBaseRounded.values()) baseTaxRateInAmount += v;

  return {
    perLineDefaultTax,
    perLineOtherTax,
    perLineBaseDefaultTax,
    taxByName,
    taxRateInAmount,
    baseTaxRateInAmount: roundHalfUp(baseTaxRateInAmount),
  };
}

// Android calculateTaxes: a line is food-stampable by the VARIANT flag when the product is a variant
// product, else by the product flag. `vfood_stampable` arrives as a per-variant CSV, so resolve the
// line's own slot by variant id; a bare "1" (single value) keeps working.
export function lineIsFoodStampable(line) {
  const m = (line && line.model) || {};
  const on = (v) => String(v == null ? "" : v).trim() === "1";
  if (String(m.isvarient) === "1" && !line.isCustomLineItem) {
    const flags = String(m.vFoodStampable == null ? "" : m.vFoodStampable).split(",");
    if (flags.length <= 1) return on(flags[0]);
    const ids = String(m.vVariantId == null ? "" : m.vVariantId).split(",");
    const i = ids.findIndex((v) => v.trim() === String(line.variantId || ""));
    return on(i >= 0 ? flags[i] : flags[0]);
  }
  return on(m.foodStampable);
}

/**
 * Android `calculateFoodStampable` — the "Food Stampable" cart-summary total (EBT-eligible money):
 *   Σ over food-stampable lines of (unit price − item discount) × count
 *   + Σ of those lines' default + other tax (each rounded)
 *   − Σ of (that net line price × count × orderDiscountPercentage)   // the order-level share
 * where unit price = orderUnitNewPrice > 0 ? orderUnitNewPrice : model.price, the item discount is
 * the $ discount when set else the % one, and orderDiscountPercentage = order discount ÷ (subtotal −
 * lottery buys) (getTaxRates). Each of the three sums is rounded before combining, like Android.
 */
export function calculateFoodStampable(cart, tax, orderDiscountPercentage = 0) {
  let price = 0;
  let taxes = 0;
  let discountPrice = 0;
  const pct = Number.isFinite(orderDiscountPercentage) ? orderDiscountPercentage : 0;
  for (const line of cart) {
    if (line.isSplitPaymentDiscount || line.isSplitPayment || line.isSplitRefund) continue;
    if (line.isScratcherPayout || line.isLotteryPayout || line.isLotteryTicket === "1") continue;
    if (!lineIsFoodStampable(line)) continue;
    let unit = toNum(line.orderUnitNewPrice) > 0 ? toNum(line.orderUnitNewPrice) : toNum((line.model || {}).price);
    const disc = toNum(line.orderDiscount) !== 0 ? toNum(line.orderDiscount) : unit * (toNum(line.orderDiscountPercent) / 100);
    unit -= disc;
    const def = tax && tax.perLineDefaultTax ? tax.perLineDefaultTax.get(line.orderId) : null;
    const others = tax && tax.perLineOtherTax ? tax.perLineOtherTax.get(line.orderId) || [] : [];
    const defAmt = roundHalfUp(def ? toNum(def.price) : 0);
    const otherAmt = roundHalfUp(others.reduce((s, t) => s + toNum(t.price), 0));
    const count = toNum(line.orderCount);
    discountPrice += unit * count * pct;
    price += unit * count;
    taxes += defAmt + otherAmt;
  }
  const safe = (v) => (Number.isFinite(v) ? roundHalfUp(v) : 0);
  return roundHalfUp(safe(price) + safe(taxes) - safe(discountPrice));
}
